import * as React from 'react';
import { plainDate, plainDateParts, type PlainDate } from '@cfl/shared';

import {
  DatePicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/cargofl-ui';

/**
 * The themed DatePicker, speaking PlainDate.
 *
 * Every date in this system is a `YYYY-MM-DD` business day in Asia/Kolkata, but the
 * picker underneath is built on a JS `Date`. Converting between them is precisely
 * where the bug this codebase keeps warning about gets in: `new Date('2026-09-11')`
 * parses as UTC midnight, and reading it back with `getFullYear` in any zone behind
 * UTC gives the 10th - the whole form silently a day out.
 *
 * So both directions go through the calendar parts, never through an instant. The
 * Date handed to the picker is constructed in LOCAL time purely so the calendar
 * highlights the right square; it is never sent anywhere.
 */
export function PlainDatePicker({
  value,
  onChange,
  id,
  disabled,
  placeholder,
  className,
  min,
  max,
}: {
  value: PlainDate | '';
  onChange: (value: PlainDate) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Inclusive bounds, as PlainDates. Days outside them are not selectable. */
  min?: PlainDate;
  max?: PlainDate;
}) {
  const asDate = React.useMemo(() => {
    if (!value) return undefined;
    const { year, month, day } = plainDateParts(value);
    // Local-time construction on purpose: this only ever feeds the calendar's
    // "which square is selected", and a UTC-midnight Date renders as the previous
    // day west of UTC.
    return new Date(year, month - 1, day);
  }, [value]);

  // Bounds compared as PlainDate strings, where lexicographic order IS chronological
  // order - no instants involved, so nothing to get wrong by a zone offset.
  const disabledDates = React.useCallback(
    (date: Date) => {
      const day = plainDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
      if (min && day < min) return true;
      if (max && day > max) return true;
      return false;
    },
    [min, max],
  );

  return (
    <DatePicker
      id={id}
      value={asDate}
      disabled={disabled}
      placeholder={placeholder}
      className={className}
      onChange={(date) => {
        if (!date) return;
        onChange(plainDate(date.getFullYear(), date.getMonth() + 1, date.getDate()));
      }}
      disabledDates={disabledDates}
    />
  );
}

/** Half-hour steps from 07:00 to 20:00 - the window a working session falls in. */
const TIME_OPTIONS = Array.from({ length: 27 }, (_, i) => {
  const minutes = 7 * 60 + i * 30;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const value = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { value, label: `${hour12}:${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}` };
});

/**
 * A themed time picker, as a Select rather than a text field.
 *
 * A session starts on the hour or the half hour; free-typing invites 9:07 and an
 * `HH:MM` the API then rejects. The list carries any value already stored, so a
 * time set outside these steps is shown rather than silently snapped to one.
 */
export function TimeSelect({
  value,
  onChange,
  id,
  disabled,
}: {
  value: string;
  onChange: (value: string) => void;
  id?: string;
  disabled?: boolean;
}) {
  const options = React.useMemo(() => {
    if (!value || TIME_OPTIONS.some((o) => o.value === value)) return TIME_OPTIONS;
    const [h, m] = value.split(':').map(Number);
    const hour12 = (h ?? 0) % 12 === 0 ? 12 : (h ?? 0) % 12;
    const label = `${hour12}:${String(m ?? 0).padStart(2, '0')}${(h ?? 0) < 12 ? 'am' : 'pm'}`;
    return [...TIME_OPTIONS, { value, label }].sort((a, b) => a.value.localeCompare(b.value));
  }, [value]);

  return (
    <Select value={value} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger id={id} className="w-full">
        <SelectValue placeholder="Pick a time" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
